import type { ReactNode } from 'react'
import { useSwipeBack } from '../useSwipeBack'
import './SwipeBackPage.css'

interface SwipeBackPageProps {
  /** The page's existing back handler — run when a committed back-swipe finishes. */
  onBack: () => void
  /** CSS selector for in-page horizontally-scrollable elements to exclude from
   *  the gesture (defaults to `.chip-row`). */
  excludeSelector?: string
  children: ReactNode
}

/** Wraps a pushed page so it can be popped with a left-edge swipe-to-go-back.
 * Thin: the whole gesture lives in `useSwipeBack`; this just
 * mounts the tracked wrapper and routes its `transitionend`. Additive — the
 * page's own back control still calls `onBack` directly. */
export default function SwipeBackPage({
  onBack,
  excludeSelector = '.chip-row',
  children,
}: SwipeBackPageProps) {
  const { containerRef, drag, onSettled } = useSwipeBack({ onBack, excludeSelector })

  return (
    <div
      ref={containerRef}
      className={`swipe-back-page ${drag.active ? 'swipe-back-page--active' : ''}`}
      style={{
        // No transform at rest: a `translateX(0)` still establishes a
        // containing block, which would re-anchor the page's `position: fixed`
        // descendants (e.g. the league sticky header) even when idle. Only take
        // a transform once a gesture is live, where moving them with the page
        // is exactly what we want.
        transform: drag.phase === 'idle' ? undefined : drag.transform,
        transition: drag.animate
          ? 'transform var(--motion-base) var(--ease-out)'
          : 'none',
      }}
      onTransitionEnd={
        drag.phase === 'settling'
          ? (e) => {
              if (e.target === e.currentTarget && e.propertyName === 'transform') {
                onSettled()
              }
            }
          : undefined
      }
    >
      {children}
    </div>
  )
}
